import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Users, Building, Medal, User as UserIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { loadCompetitionStandings, loadStandingUsers } from '@/lib/firebase/queries/competitionStandings';
import { formatAverage, type ContainerStandingRow } from '@/lib/competitions/competitionRanking';
import type { Competition } from '@/types/competitions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CompetitionParticipant {
  userId: string;
  firstName: string;
  lastName: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  photoURL: string;
  completedHabitsCount: number;
  rank: number;
}

interface GroupRanking {
  id: string;
  name: string;
  completedHabitsCount: number;
  memberCount: number;
  /** completedHabitsCount / memberCount: the ranking score (namat#32). */
  average: number;
  rank: number;
}

type SectionRanking = GroupRanking;

interface TopPerformersData {
  topIndividuals: CompetitionParticipant[];
  topGroups: GroupRanking[];
  topSections: SectionRanking[];
}

interface TopPerformersWidgetProps {
  competition: Competition;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TopPerformersWidget({ competition, organizationId, open, onOpenChange }: TopPerformersWidgetProps) {
  const [performersData, setPerformersData] = useState<TopPerformersData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  useEffect(() => {
    if (open && competition) {
        loadTopPerformersData();
    }
  }, [open, competition]);

  const loadTopPerformersData = async () => {
    try {
      setIsLoading(true);
      
      if (!competition || !organizationId) return;

      // Same standings as beneficiaries, mobile and the server (namat#29/#32).
      // The viewing manager is not a participant, hence no current user.
      const standings = await loadCompetitionStandings(competition.id, organizationId, '', competition);
      const top = standings.individuals.slice(0, 10);
      const users = await loadStandingUsers(top.map(p => p.userId));
      const topIndividuals: CompetitionParticipant[] = top.map(p => ({
        userId: p.userId,
        ...(users.get(p.userId) || { firstName: '', lastName: '', firstNameEn: '', lastNameEn: '', firstNameAr: '', lastNameAr: '', photoURL: '' }),
        completedHabitsCount: p.completed,
        rank: p.rank,
      }));
      const toRanking = (r: ContainerStandingRow): GroupRanking => ({
        id: r.id, name: r.name, completedHabitsCount: r.completed, memberCount: r.memberCount, average: r.average, rank: r.rank,
      });
      const topGroups = standings.groups.slice(0, 10).map(toRanking);
      const topSections = standings.sections.slice(0, 10).map(toRanking);

      setPerformersData({
        topIndividuals,
        topGroups,
        topSections
      });
    } catch (error) {
      console.error('Failed to load top performers data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <div className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">#{index + 1}</div>;
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-[#7dda55]" />
            {competition.name} - {t('dashboard.manager.competitions.results') || 'Results'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Tabs defaultValue="individuals" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                {isRTL ? (
                  <>
                    <TabsTrigger value="sections" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {t('dashboard.competition.sections')}
                    </TabsTrigger>
                    <TabsTrigger value="groups" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t('dashboard.competition.groups')}
                    </TabsTrigger>
                    <TabsTrigger value="individuals" className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      {t('dashboard.competition.individuals')}
                    </TabsTrigger>
                  </>
                ) : (
                  <>
                    <TabsTrigger value="individuals" className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      {t('dashboard.competition.individuals')}
                    </TabsTrigger>
                    <TabsTrigger value="groups" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t('dashboard.competition.groups')}
                    </TabsTrigger>
                    <TabsTrigger value="sections" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {t('dashboard.competition.sections')}
                    </TabsTrigger>
                  </>
                )}
              </TabsList>

              <TabsContent value="individuals" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-[#7dda55]" />
                  {t('dashboard.manager.competitions.topIndividuals') || 'Top 10 Individuals'}
                </h3>
                {performersData?.topIndividuals && performersData.topIndividuals.length > 0 ? (
        <div className="space-y-3">
                    {performersData.topIndividuals.map((participant, index) => (
                      <div
                        key={participant.userId}
                        className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                <div className="flex items-center gap-3">
                  {getRankIcon(index)}
                          <Avatar className="w-10 h-10">
                            <AvatarImage 
                              src={participant.photoURL} 
                              alt={`${participant.firstNameEn || participant.firstName} ${participant.lastNameEn || participant.lastName}`}
                            />
                            <AvatarFallback className="bg-[#7dda55] text-white">
                              {participant.photoURL ? (
                                getInitials(
                                  participant.firstNameEn || participant.firstName || '', 
                                  participant.lastNameEn || participant.lastName || ''
                                )
                              ) : (
                                <UserIcon className="h-5 w-5" />
                              )}
                    </AvatarFallback>
                  </Avatar>
                </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            {isRTL 
                              ? `${participant.firstNameAr || participant.firstName} ${participant.lastNameAr || participant.lastName}`
                              : `${participant.firstNameEn || participant.firstName} ${participant.lastNameEn || participant.lastName}`
                            }
                  </div>
                  <div className="text-sm text-muted-foreground">
                            {t('dashboard.competition.completedHabits') || 'Completed Habits'}: {participant.completedHabitsCount}
                  </div>
                </div>
                <Badge variant="secondary" className="text-lg font-bold text-[#7dda55] bg-[#7dda55]/10">
                          #{participant.rank}
                </Badge>
              </div>
                    ))}
                  </div>
          ) : (
                           <div className="text-center py-8 text-muted-foreground">
                    <UserIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('dashboard.competition.noParticipants') || 'No participants yet'}</p>
             </div>
          )}
              </TabsContent>

              <TabsContent value="groups" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#7dda55]" />
                  {t('dashboard.manager.competitions.topGroups') || 'Top 10 Groups'}
                </h3>
                {performersData?.topGroups && performersData.topGroups.length > 0 ? (
        <div className="space-y-3">
                    {performersData.topGroups.map((group, index) => (
                      <div
                        key={group.id}
                        className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                <div className="flex items-center gap-3">
                  {getRankIcon(index)}
                          <div className="flex items-center justify-center w-10 h-10 bg-[#7dda55] rounded-full">
                            <Users className="h-5 w-5 text-white" />
                          </div>
                </div>
                        <div className="flex-1">
                          <div className="font-medium">{group.name}</div>
                  <div className="text-sm text-muted-foreground">
                            {t('dashboard.competition.perMember')}: {formatAverage(group.average)} • {t('dashboard.competition.total')} {group.completedHabitsCount} • {group.memberCount} {t('dashboard.competition.members')}
                  </div>
                </div>
                <Badge variant="secondary" className="text-lg font-bold text-[#7dda55] bg-[#7dda55]/10">
                          #{group.rank}
                </Badge>
              </div>
                    ))}
                  </div>
          ) : (
                           <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('dashboard.competition.noParticipants') || 'No participants yet'}</p>
             </div>
          )}
              </TabsContent>

              <TabsContent value="sections" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Building className="h-5 w-5 text-[#7dda55]" />
                  {t('dashboard.manager.competitions.topSections') || 'Top 10 Sections'}
                </h3>
                {performersData?.topSections && performersData.topSections.length > 0 ? (
        <div className="space-y-3">
                    {performersData.topSections.map((section, index) => (
                      <div
                        key={section.id}
                        className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                <div className="flex items-center gap-3">
                  {getRankIcon(index)}
                          <div className="flex items-center justify-center w-10 h-10 bg-[#7dda55] rounded-full">
                            <Building className="h-5 w-5 text-white" />
                          </div>
                </div>
                        <div className="flex-1">
                          <div className="font-medium">{section.name}</div>
                  <div className="text-sm text-muted-foreground">
                            {t('dashboard.competition.perMember')}: {formatAverage(section.average)} • {t('dashboard.competition.total')} {section.completedHabitsCount} • {section.memberCount} {t('dashboard.competition.members')}
                  </div>
                </div>
                <Badge variant="secondary" className="text-lg font-bold text-[#7dda55] bg-[#7dda55]/10">
                          #{section.rank}
                </Badge>
              </div>
                    ))}
                  </div>
          ) : (
                           <div className="text-center py-8 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('dashboard.competition.noParticipants') || 'No participants yet'}</p>
             </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 